import React, { useState, useEffect } from "react";
import OfflineFallback from "./components/OfflineFallback";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './Layout';
import Dashboard from './pages/Dashboard';
import TransactionsPage from './pages/TransactionsPage';
import ContributionsPage from './pages/ContributionsPage';
import ContributionDrivesPage from './pages/ContributionDrivesPage';
import ContributionBalancePage from './pages/ContributionBalancePage';
import PayContributionPage from './pages/PayContributionPage';
import MembersPage from './pages/MembersPage';
// import DrivesPage from './pages/DrivesPage';
import SettingsPage from './pages/SettingsPage';
import Login from './pages/Login';
import UpgradePage from './pages/UpgradePage';
import FeatureProtectedRoute from './components/FeatureProtectedRoute';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import FinesPage from './pages/FinesPage';
import LoansPage from './pages/LoansPage';
import TableBankingLoanPage from './pages/TableBankingLoanSection.js'
import LoansTypesPage from './pages/LoansTypesPage';
import LoanRepaymentsPage from './pages/LoanRepaymentsPage';
import LoanDetailsPage from './pages/LoanDetailsPage';
import LoanApprovalDashboard from './pages/LoanApprovalDashboard';
import LoanApprovalsPage from './pages/LoanApprovalsPage';
import MerryGoRoundPage from './pages/MerryGoRoundPage';
import BulkSmsPage from './pages/BulkSmsPage';
import SmsLogsPage from './pages/SmsLogsPage.tsx';
import WelfarePage from './pages/WelfarePage';
import WelfareAssistancePage from './pages/WelfareAssistancePage';
import MeetingNotesPage from './pages/MeetingNotesPage';
import MeetingsPage from './pages/MeetingsPageNew';
import CreateMeetingPage from './pages/CreateMeetingPage';
import NotificationsPage from './pages/NotificationsPage';
import DocumentsPage from './pages/DocumentsPage';
import AdminsPage from './pages/AdminsPage';
import SharesPage from './pages/SharesPage';
import DisbursementPage from './pages/DisbursementPage';
import LoanDisbursementsPage from './pages/LoanDisbursementsPage';
import DocumentationPage from './pages/DocumentationPage';
import DocumentationIndexPage from './pages/DocumentationIndexPage';
import OnboardingPage from './pages/OnboardingPage';
import StatementDownloadPage from './pages/StatementDownloadPage';

import ExpensesPage from './pages/ExpensesPage';
import AssetsPage from './pages/AssetsPage';
import AssetDetailsPage from './pages/AssetDetailsPage';
import LoanEligibilityPage from './pages/LoanEligibilityPage';
import GuarantorRequestsPage from './pages/GuarantorRequestsPage';
import RolesPage from './pages/RolesPage';
import RoleFormPage from './pages/RoleFormPage';
import TableBankingLoanDetailsPage from './pages/TableBankingLoanDetailsPage.js';
import SubgroupsPage from './pages/SubgroupsPage';
import MySubgroupPage from './pages/MySubgroupPage';
import HousesPage from './pages/HousesPage';
import MyHousePage from './pages/MyHousePage';
import LoanArrearsPage from './pages/LoanArrearsPage';
import LoanFeesPage from './pages/LoanFeesPage';
import MemberProfilePage from './pages/MemberProfilePage';
import ID from './pages/ID';
import DownloadAll from './pages/DownloadAll';
import {ThemeProvider} from './context/ThemeContext.tsx';
import { AuthProvider } from './context/AuthContext';

const isAuthenticated = () => {
  const auth = localStorage.getItem('userToken');
  return auth && auth !== null;
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  
  if (!isAuthenticated()) {
    // Save the attempted URL to redirect back after login
    localStorage.setItem('redirectAfterLogin', location.pathname);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
};

// Public Route component (for login page)
const PublicRoute = ({ children }) => {
  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }
  return children;
};


const queryClient = new QueryClient();

export default function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOnline) {
    return <OfflineFallback />;
  }

  return (
    <ThemeProvider>
    <AuthProvider>
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" reverseOrder={false} />
      <Router>
        <Routes>
          {/* Public route */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />

          {/* Upgrade route */}
          <Route 
            path="/upgrade" 
            element={
              <ProtectedRoute>
                <UpgradePage />
              </ProtectedRoute>
            } 
          />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <Layout>
                  <TransactionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/disbursement"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="money_disbursement">
                    <DisbursementPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/members"
            element={
              <ProtectedRoute>
                <Layout>
                  <MembersPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/members/:memberId"
            element={
              <ProtectedRoute>
                <Layout>
                  <MemberProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contributions"
            element={
              <ProtectedRoute>
                <Layout>
                  <ContributionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contribution-drives"
            element={
              <ProtectedRoute>
                <Layout>
                  <ContributionDrivesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contribution-balance"
            element={
              <ProtectedRoute>
                <Layout>
                  <ContributionBalancePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pay-contribution"
            element={
              <ProtectedRoute>
                <Layout>
                  <PayContributionPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/fines"
            element={
              <ProtectedRoute>
                <Layout>
                  <FinesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/member-loans"
            element={
              <ProtectedRoute>
                <Layout>
                  <LoansPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/table-banking-loans"
            element={
              <ProtectedRoute>
                <Layout>
                  <TableBankingLoanPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-types"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoansTypesPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-repayments"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanRepaymentsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-details/:loanId"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanDetailsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

            <Route
            path="/table-loan-details/:loanId"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <TableBankingLoanDetailsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-approval-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanApprovalDashboard />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-approvals"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanApprovalsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-disbursements"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanDisbursementsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-arrears"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanArrearsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-fees"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanFeesPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
        
          {/* <Route
            path="/drives"
            element={
              <ProtectedRoute>
                <Layout>
                  <DrivesPage />
                </Layout>
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/admins"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="multiple_admin_access">
                    <AdminsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="multiple_admin_access">
                    <RolesPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/create"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="multiple_admin_access">
                    <RoleFormPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/:roleId/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="multiple_admin_access">
                    <RoleFormPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/merry-go-round"
            element={
              <ProtectedRoute>
                <Layout>
                  <MerryGoRoundPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk-sms"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="bulk_sms">
                    <BulkSmsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/sms-logs"
            element={
              <ProtectedRoute>
                <Layout>
                  <SmsLogsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/welfare/requests"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="welfare_management">
                    <WelfareAssistancePage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/welfare"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="welfare_management">
                    <WelfarePage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/meeting-notes"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="meeting_minutes">
                    <MeetingNotesPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/meetings"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="meetings">
                    <MeetingsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/meetings/create"
            element={
              <ProtectedRoute>
                <FeatureProtectedRoute requiredFeature="meetings">
                  <CreateMeetingPage />
                </FeatureProtectedRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Layout>
                  <NotificationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="documents_management">
                    <DocumentsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="expenses_management">
                    <ExpensesPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/assets"
            element={
              <ProtectedRoute requiredFeature="assets_management">
                <Layout>
                  <AssetsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/assets/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <AssetDetailsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Redirect old loan settings routes to Settings page with loans tab */}
          <Route
            path="/loan-settings"
            element={<Navigate to="/settings?tab=loans" replace />}
          />

          <Route
            path="/table-banking-settings"
            element={<Navigate to="/settings?tab=loans" replace />}
          />

          <Route
            path="/shares"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="shares_management">
                    <SharesPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-eligibility"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <LoanEligibilityPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/guarantor-requests"
            element={
              <ProtectedRoute>
                <Layout>
                  <FeatureProtectedRoute requiredFeature="basic_loan_management">
                    <GuarantorRequestsPage />
                  </FeatureProtectedRoute>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/subgroups"
            element={
              <ProtectedRoute>
                <Layout>
                  <SubgroupsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-subgroup"
            element={
              <ProtectedRoute>
                <Layout>
                  <MySubgroupPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/houses"
            element={
              <ProtectedRoute>
                <Layout>
                  <HousesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-house"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyHousePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Documentation routes */}
          <Route
            path="/documentation"
            element={
              <ProtectedRoute>
                <Layout>
                  <DocumentationIndexPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/documentation/:page"
            element={
              <ProtectedRoute>
                <DocumentationPage />
              </ProtectedRoute>
            }
          />

          <Route path="/onboard/:token" element={<OnboardingPage />} />

          <Route
            path="/id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ID />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/id/:memberId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ID />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/download"
            element={
              <ProtectedRoute>
                <DownloadAll />
              </ProtectedRoute>
            }
          />

          {/* Public statement download route (token-based, no auth required) */}
          <Route
            path="/statement/:token"
            element={<StatementDownloadPage />}
          />

          {/* Catch all route - redirect to login */}
          <Route
            path="*"
            element={<Navigate to="/login" replace />}
          />
        </Routes>
      </Router>
    </QueryClientProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}